/**
 * BotControl v3 API 服务层
 *
 * 封装真实 v3 API 调用，保持与现有组件兼容的接口格式。
 * 所有返回格式: { success: true, data: T } 或 { success: false, error: { code, message } }
 *
 * @see src/services/api/v3/ — 底层请求实现
 */

import * as v3Api from './api/v3';

// ─── 辅助函数 ────────────────────────────────────────────────────────────────

/**
 * 将真实 API 返回值包装为统一响应格式 { success, data }
 * 捕获异常并返回 { success: false, error }
 * @template T
 * @param {Promise<T>} promise - 真实 API 调用
 * @returns {Promise<{success: true, data: T} | {success: false, error: {code: string, message: string}}>}
 */
async function wrap(promise) {
  try {
    const data = await promise;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: {
        code: error.code || 'UNKNOWN',
        message: error.message || '未知错误',
      },
    };
  }
}

// ─── API 函数 ────────────────────────────────────────────────────────────────

/**
 * 获取所有 Bot 列表
 * @returns {Promise<{success: true, data: {bots: Array}}>}
 */
export function fetchBots() {
  return wrap(v3Api.fetchBots().then(bots => ({ bots })));
}

/**
 * 获取单个 Bot 信息
 * @param {string} id
 * @returns {Promise<{success: true, data: object}>}
 */
export function fetchBotInfo(id) {
  return wrap(v3Api.fetchBot(id));
}

/**
 * 获取在线 Bot 数量
 * @returns {Promise<{success: true, data: {count: number}}>}
 */
export function fetchOnlineCount() {
  return wrap(v3Api.fetchOnlineCount().then(count => ({ count })));
}

/**
 * 获取 Bot 总数
 * @returns {Promise<{success: true, data: {count: number}}>}
 */
export function fetchTotalCount() {
  return wrap(v3Api.fetchTotalCount().then(count => ({ count })));
}

/**
 * 同时获取在线数和总数（直接返回，无需 unwrap）
 * @returns {Promise<{onlineCount: number, totalCount: number}>}
 */
export function fetchStats() {
  return v3Api.fetchStats();
}

/**
 * 创建新 Bot
 * @param {{name: string, host: string, port: number, password?: string}} values
 * @returns {Promise<{success: true, data: {message: string, bot_id: string}}>}
 */
export function createBot(values) {
  return wrap(v3Api.createBot(values));
}

/**
 * 启动 Bot
 * @param {string} id
 * @param {{force?: boolean}} [options]
 * @returns {Promise<{success: true, data: {message: string}} | {success: false, error: {code: string, message: string}}>}
 */
export function startBot(id, options = {}) {
  return wrap(v3Api.startBot(id, options.force));
}

/**
 * 停止 Bot
 * @param {string} id
 * @returns {Promise<{success: true, data: {message: string}}>}
 */
export function stopBot(id) {
  return wrap(v3Api.stopBot(id));
}

/**
 * 删除 Bot
 * @param {string} id
 * @param {{force?: boolean}} [options]
 * @returns {Promise<{success: true, data: {message: string}}>}
 */
export function deleteBot(id, options = {}) {
  return wrap(v3Api.deleteBot(id, options.force));
}

/**
 * 发送命令到 Bot
 * @param {string} id
 * @param {string} command
 * @returns {Promise<{success: true, data: {message: string}}>}
 */
export function sendBotCommand(id, command) {
  return wrap(v3Api.sendCommand(id, command));
}

/**
 * 执行 Bot 预设动作
 * @param {string} id
 * @param {string} action
 * @param {Record<string, any>} [params]
 * @returns {Promise<{success: true, data: {message: string}}>}
 */
export function executeBotAction(id, action, params = {}) {
  return wrap(v3Api.executeAction(id, action, params));
}

/**
 * 获取可用动作模板列表
 * @returns {Promise<{success: true, data: Array}>}
 */
export function fetchActions() {
  return wrap(v3Api.fetchActions());
}

/**
 * 获取 Bot 日志
 * @param {string} id
 * @param {{lines?: number}} [options]
 * @returns {Promise<{success: true, data: {logs: Array}}>}
 */
export function getLogs(id, options = {}) {
  return wrap(v3Api.fetchLogs(id, options.lines).then(logs => ({ logs })));
}

/**
 * 获取 Bot 聊天记录
 * @param {string} id
 * @param {{lines?: number}} [options]
 * @returns {Promise<{success: true, data: {chats: Array}}>}
 */
export function getChats(id, options = {}) {
  return wrap(v3Api.fetchChats(id, options.lines).then(chats => ({ chats })));
}

/**
 * 获取 Bot 配置
 * @param {string} id
 * @returns {Promise<{success: true, data: object}>}
 */
export function fetchBotConfig(id) {
  return wrap(v3Api.fetchBotConfig(id));
}

/**
 * 更新 Bot 配置
 * @param {string} id
 * @param {object} config
 * @returns {Promise<{success: true, data: object}>}
 */
export function updateBotConfig(id, config) {
  return wrap(v3Api.updateBotConfig(id, config));
}
